import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EmployeeLeaveTable = ({ employees, onApprove, onReject, onViewDetails }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const getSortedEmployees = () => {
    let filtered = employees?.filter(emp => 
      emp?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );

    return filtered?.sort((a, b) => {
      if (sortConfig?.key === 'name') {
        return sortConfig?.direction === 'asc' 
          ? a?.name?.localeCompare(b?.name)
          : b?.name?.localeCompare(a?.name);
      }
      if (sortConfig?.key === 'balance') {
        return sortConfig?.direction === 'asc'
          ? a?.annualBalance - b?.annualBalance
          : b?.annualBalance - a?.annualBalance;
      }
      return 0;
    });
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending' },
      approved: { bg: 'bg-success/10', text: 'text-success', label: 'Approved' },
      rejected: { bg: 'bg-error/10', text: 'text-error', label: 'Rejected' }
    };
    const config = configs?.[status] || configs?.pending;
    return (
      <span className={`px-2 py-1 rounded-md caption text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const sortedEmployees = getSortedEmployees();

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
            Employee Leave Records
          </h3>
          <div className="relative w-full md:w-64">
            <Icon 
              name="Search" 
              size={18} 
              color="var(--color-muted-foreground)" 
              className="absolute left-3 top-1/2 -translate-y-1/2"
            />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md caption text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 font-body font-medium text-sm text-foreground hover:text-primary transition-smooth"
                >
                  Employee
                  <Icon 
                    name={sortConfig?.key === 'name' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                    color="currentColor"
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left caption text-sm font-medium text-foreground">
                Department
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('balance')}
                  className="flex items-center gap-2 font-body font-medium text-sm text-foreground hover:text-primary transition-smooth"
                >
                  Annual Balance
                  <Icon 
                    name={sortConfig?.key === 'balance' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                    color="currentColor"
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left caption text-sm font-medium text-foreground">
                Sick Balance
              </th>
              <th className="px-6 py-3 text-left caption text-sm font-medium text-foreground">
                Recent Request
              </th>
              <th className="px-6 py-3 text-left caption text-sm font-medium text-foreground">
                Status
              </th>
              <th className="px-6 py-3 text-right caption text-sm font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedEmployees?.map((employee) => (
              <tr key={employee?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Image
                      src={employee?.avatar}
                      alt={employee?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-body font-medium text-sm text-foreground">
                        {employee?.name}
                      </p>
                      <p className="caption text-xs text-muted-foreground">
                        {employee?.employeeId}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="caption text-sm text-foreground">{employee?.department}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="data-text text-sm font-medium text-foreground">
                    {employee?.annualBalance} days
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="data-text text-sm font-medium text-foreground">
                    {employee?.sickBalance} days
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="caption text-sm text-foreground">{employee?.recentRequest?.type}</p>
                    <p className="caption text-xs text-muted-foreground">
                      {employee?.recentRequest?.dates}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(employee?.recentRequest?.status)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {employee?.recentRequest?.status === 'pending' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onApprove(employee?.id)}
                          iconName="Check"
                          iconPosition="left"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReject(employee?.id)}
                          iconName="X"
                          iconPosition="left"
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(employee?.id)}
                      iconName="Eye"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-border">
        {sortedEmployees?.map((employee) => (
          <div key={employee?.id} className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Image
                src={employee?.avatar}
                alt={employee?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-sm text-foreground mb-1">
                  {employee?.name}
                </p>
                <p className="caption text-xs text-muted-foreground mb-1">
                  {employee?.employeeId} • {employee?.department}
                </p>
                {getStatusBadge(employee?.recentRequest?.status)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="caption text-xs text-muted-foreground mb-1">Annual Balance</p>
                <p className="data-text text-sm font-medium text-foreground">
                  {employee?.annualBalance} days
                </p>
              </div>
              <div>
                <p className="caption text-xs text-muted-foreground mb-1">Sick Balance</p>
                <p className="data-text text-sm font-medium text-foreground">
                  {employee?.sickBalance} days
                </p>
              </div>
            </div>
            <div className="mb-3">
              <p className="caption text-xs text-muted-foreground mb-1">Recent Request</p>
              <p className="caption text-sm text-foreground">{employee?.recentRequest?.type}</p>
              <p className="caption text-xs text-muted-foreground">{employee?.recentRequest?.dates}</p>
            </div>
            {employee?.recentRequest?.status === 'pending' && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => onApprove(employee?.id)}
                  iconName="Check"
                  iconPosition="left"
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => onReject(employee?.id)}
                  iconName="X"
                  iconPosition="left"
                >
                  Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeLeaveTable;